import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import * as transactionsMiddleware from "middleware/transactions";
import type { ProofOfDeliveryApiObject } from "middleware/transactions";
import DeliveryEvidence, {
  type DeliveryEvidenceItem,
} from "./DeliveryEvidence";

const STORAGE_KEY_PREFIX = "deliveryEvidence-";

const PHOTO_SIGN_TYPES = ["PHOTO", "SIGN"];

function isProofOfDeliveryObject(
  raw: unknown
): raw is ProofOfDeliveryApiObject {
  return (
    raw != null &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    "latitude" in (raw as object) &&
    "longitude" in (raw as object)
  );
}

function normalizeProofItems(
  raw: { type: string; value: string }[]
): DeliveryEvidenceItem[] {
  return raw
    .filter(
      (item) =>
        item.value &&
        PHOTO_SIGN_TYPES.includes(String(item.type).toUpperCase())
    )
    .map((item) => ({
      type: String(item.type).toUpperCase(),
      value: String(item.value),
    }));
}

function proofObjectToItems(pod: ProofOfDeliveryApiObject): DeliveryEvidenceItem[] {
  const items: DeliveryEvidenceItem[] = [];
  if (pod.photo) items.push({ type: "PHOTO", value: String(pod.photo) });
  if (pod.sign) items.push({ type: "SIGN", value: String(pod.sign) });
  return items;
}

const DeliveryEvidenceContainer: React.FC = (): JSX.Element => {
  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const evidenceId = params.get("evidenceId");
  const transactionId = params.get("transactionId");
  const [items, setItems] = useState<DeliveryEvidenceItem[]>([]);
  const [watermarkMeta, setWatermarkMeta] = useState<{
    latitude?: number;
    longitude?: number;
    finishByAdmin?: boolean;
    timestamp?: Date;
  }>({});
  const [fetchDone, setFetchDone] = useState(false);
  const readStorageRef = useRef<string | null>(null);
  const fetchedProofRef = useRef<string | null>(null);

  useEffect(() => {
    if (!transactionId) return;
    if (fetchedProofRef.current === transactionId) {
      setFetchDone(true);
      return;
    }
    fetchedProofRef.current = transactionId;
    setFetchDone(false);

    const loadEvidence = async () => {
      try {
        const [proofOfDeliveryRes, deliveryProofRes] = await Promise.all([
          transactionsMiddleware.getProofOfDelivery(transactionId),
          transactionsMiddleware.getProof(transactionId),
        ]);

        const result = proofOfDeliveryRes.data?.result;
        const rawProof = result?.proofOfDelivery;
        const isObjectFormat = isProofOfDeliveryObject(rawProof);

        const proofList =
          proofOfDeliveryRes.status === 200 && result
            ? isObjectFormat
              ? proofObjectToItems(rawProof)
              : Array.isArray(rawProof)
                ? normalizeProofItems(rawProof)
                : []
            : [];

        const deliveryProofLink =
          deliveryProofRes.status === 200 &&
          deliveryProofRes.data?.result?.link
            ? deliveryProofRes.data.result.link
            : null;

        if (proofOfDeliveryRes.status === 200 && result) {
          const lat = isObjectFormat ? rawProof.latitude : result.latitude;
          const lng = isObjectFormat ? rawProof.longitude : result.longitude;
          const finishByAdmin = isObjectFormat && rawProof?.finishByAdmin != null
            ? rawProof.finishByAdmin
            : result.finishByAdmin;
          const rawFromResult = result.timestamp ?? (result as Record<string, unknown>).delivery_timestamp;
          const rawTimestamp = isObjectFormat && rawProof?.timestamp != null
            ? rawProof.timestamp
            : rawFromResult;
          const timestamp = rawTimestamp != null
            ? (typeof rawTimestamp === "string" ? new Date(rawTimestamp) : rawTimestamp)
            : undefined;
          const timestampDate = timestamp instanceof Date && !Number.isNaN(timestamp.getTime())
            ? timestamp
            : undefined;
          setWatermarkMeta({
            latitude: lat,
            longitude: lng,
            finishByAdmin: Boolean(finishByAdmin),
            timestamp: timestampDate,
          });
        }

        let itemsToSet: DeliveryEvidenceItem[];
        if (proofList.length > 0) {
          itemsToSet = proofList.map((item, i) =>
            item.type === "PHOTO" && i === 0 && deliveryProofLink
              ? { ...item, value: deliveryProofLink }
              : item
          );
        } else if (deliveryProofLink) {
          itemsToSet = [{ type: "PHOTO", value: deliveryProofLink }];
        } else {
          itemsToSet = [];
        }
        const singleIndexParam = params.get("singleIndex");
        const singleIndex = singleIndexParam !== null ? parseInt(singleIndexParam, 10) : -1;
        if (!Number.isNaN(singleIndex) && singleIndex >= 0 && singleIndex < itemsToSet.length) {
          itemsToSet = [itemsToSet[singleIndex]];
        }
        setItems(itemsToSet);
      } catch {
        setItems([]);
      } finally {
        setFetchDone(true);
      }
    };

    loadEvidence();
  }, [transactionId, params]);

  useEffect(() => {
    if (transactionId || !evidenceId) return;
    if (readStorageRef.current === evidenceId) return;
    readStorageRef.current = evidenceId;

    try {
      const raw = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${evidenceId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        let fromStorage: DeliveryEvidenceItem[] = [];
        if (Array.isArray(parsed)) {
          const withTypeValue = parsed.filter(
            (x: unknown) =>
              x && typeof x === "object" && "type" in x && "value" in x
          );
          if (withTypeValue.length > 0) {
            fromStorage = withTypeValue.map((x: { type: string; value: string }) => ({
              type: String(x.type ?? "").toUpperCase(),
              value: String(x.value ?? ""),
            }));
          } else {
            const asUrls = parsed.filter((x: unknown) => typeof x === "string");
            const types = ["PHOTO", "SIGN"];
            fromStorage = asUrls.map((value: string, i: number) => ({
              type: types[i] ?? "PHOTO",
              value,
            }));
          }
        }
        const singleIndexParam = params.get("singleIndex");
        const singleIndex = singleIndexParam !== null ? parseInt(singleIndexParam, 10) : -1;
        if (!Number.isNaN(singleIndex) && singleIndex >= 0 && singleIndex < fromStorage.length) {
          fromStorage = [fromStorage[singleIndex]];
        }
        if (fromStorage.length > 0) setItems(fromStorage);
      }
    } catch {
      setItems([]);
    }

    const key = `${STORAGE_KEY_PREFIX}${evidenceId}`;
    const timeoutId = setTimeout(() => sessionStorage.removeItem(key), 3000);
    return () => clearTimeout(timeoutId);
  }, [evidenceId, transactionId, params]);

  const loading = Boolean(
    transactionId && items.length === 0 && !fetchDone
  );

  return (
    <DeliveryEvidence
      items={items}
      loading={loading}
      watermarkMeta={watermarkMeta}
    />
  );
};

export default DeliveryEvidenceContainer;
